import { PlusOutlined, DeleteOutlined } from '@ant-design/icons'
import { Button, Tooltip } from 'antd'
import { observer } from 'mobx-react-lite'
import { IoSaveOutline } from 'react-icons/io5'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useNotification } from '@/Ship/Hooks/useNotification'
import { FormSection } from '@/Ship/Components/Form/FormSection'
import { AppDrawer, DEFAULT_DETAIL_WIDTH } from '@/Ship/Components/UI/AppDrawer/AppDrawer'
import { DrawerDirtyGuard } from '@/Ship/Components/UI/AppDrawer/DrawerDirtyGuard'
import { cx } from '@/Ship/Helpers/cssClass'

import { clusterCodeOf, regionFilterFor } from '../Helpers/clusterRegion'
import { AssignmentResource } from '../Resources/AssignmentResource'

import { MultiAssignmentFieldCell } from './MultiAssignmentFieldCell'

import type { MultiAssignmentDrawerStore } from '../Stores/MultiAssignmentDrawerStore'

import styles from './MultiAssignmentDrawer.module.css'

interface Props {
    store: MultiAssignmentDrawerStore
    onCreated?: () => void
}

/** Field def from the resource, so the drawer reuses the exact same select/inputs. */
const fieldDef = (name: string): Record<string, any> => (AssignmentResource.fields as any)?.[name] ?? { type: 'string' }

/**
 * Resolve the numeric id from a select value that may be a resource object or an
 * IRI string. Needed for the typicalRoute filter (backend's generic filter binds
 * loading_point_id/unloading_point_id integer FK columns as-is, no IRI resolution —
 * see the same helper in AssignmentCreateTemplate).
 */
const idOf = (value: any): number | null => {
    if (!value || typeof value === 'string') return null
    return value.id ?? null
}

/**
 * Drawer for creating MANY standalone tasks at once: one set of shared fields
 * (cluster/region, dispatcher, hired/carrier, objects, responsible, points, route, date, weight,
 * trips) plus a list of driver/vehicle rows. Submits a single batch request.
 * Each row becomes one task with status «Призначено виконавець».
 *
 * Оформлення — як у картки створення задачі: AppDrawer стандартної ширини, кнопка дії в шапці,
 * секції FormSection у тому самому порядку полів; закриття з уже введеним питає
 * підтвердження (DrawerDirtyGuard).
 */
export const MultiAssignmentDrawer = observer(({ store, onCreated }: Props) => {
    const { showSuccess, showError } = useNotification()
    const notify = { success: showSuccess, error: showError }

    // Стабільний onChange на кожне спільне поле.
    //
    // Інлайнова стрілка створювала нову функцію на кожен рендер drawer-а, а
    // setShared перерендерює drawer на кожен символ — через це комірка поля
    // перестворювала свій reaction і втрачала фокус під час набору. Кеш за
    // іменем поля дає одну й ту саму функцію на весь час життя drawer-а.
    const sharedHandlers = useRef<Record<string, (value: any) => void>>({})
    const onShared = useCallback(
        (field: string) => {
            sharedHandlers.current[field] ??= (value: any) => store.setShared(field, value)
            return sharedHandlers.current[field]!
        },
        [store]
    )

    // Raw loadingPoint/unloadingPoint objects (not just the IRI store.shared holds),
    // needed to filter typicalRoute by numeric id — mirrors AssignmentCreateTemplate.
    const [loadingPoint, setLoadingPoint] = useState<any>(null)
    const [unloadingPoint, setUnloadingPoint] = useState<any>(null)
    const loadingPointId = idOf(loadingPoint)
    const unloadingPointId = idOf(unloadingPoint)

    // Clear the previously chosen typical route when the points pair changes —
    // it no longer matches the new filter (skip first render so nothing fires
    // before the user touches either point).
    const prevPointsKey = useRef<string>(`${loadingPointId}|${unloadingPointId}`)
    const pointsInitialised = useRef(false)
    useEffect(() => {
        const pointsKey = `${loadingPointId}|${unloadingPointId}`
        if (pointsInitialised.current && prevPointsKey.current !== pointsKey && store.shared['typicalRoute']) {
            store.setShared('typicalRoute', null)
        }
        prevPointsKey.current = pointsKey
        pointsInitialised.current = true
    }, [store, loadingPointId, unloadingPointId])

    // Перебудовуємо лише коли реально змінилась пара пунктів — інакше кожен рендер
    // давав новий об'єкт inputProps і зайвий запис у поле комірки.
    const typicalRouteDef = useMemo(
        () => ({
            ...fieldDef('typicalRoute'),
            inputProps: {
                ...fieldDef('typicalRoute')['inputProps'],
                filter: {
                    ...(loadingPointId ? { loadingPoint: loadingPointId } : {}),
                    ...(unloadingPointId ? { unloadingPoint: unloadingPointId } : {}),
                },
            },
        }),
        [loadingPointId, unloadingPointId]
    )

    // Регіон — лише серед регіонів обраного кластера; без кластера поле заблоковане.
    // Скидання регіону при зміні кластера робить стор (setShared).
    const clusterCode = clusterCodeOf(store.shared['cluster'])
    const regionDef = useMemo(
        () => ({
            ...fieldDef('region'),
            inputProps: {
                ...fieldDef('region')['inputProps'],
                filter: regionFilterFor(clusterCode),
                disabled: !clusterCode,
            },
        }),
        [clusterCode]
    )

    const driverFilter = {
        filterRoles: { op: 'like', value: 'ROLE_DRIVER' },
        status: 'active',
        ...(store.isHired && store.carrierIri ? { 'carrier.code': store.carrierIri.split('/').pop() } : {}),
    }
    const vehicleFilter = store.isHired && store.carrierIri ? { carrier: store.carrierIri } : {}
    const rowsLocked = store.isHired && !store.carrierIri

    const shared = (name: string, def: Record<string, any> = fieldDef(name)) => (
        <MultiAssignmentFieldCell fieldName={name} label={def['label']} fieldDef={def} onChange={onShared(name)} />
    )

    return (
        <AppDrawer
            title="Створити кілька завдань"
            placement="right"
            size={DEFAULT_DETAIL_WIDTH}
            open={store.open}
            onClose={() => store.closeDrawer()}
            destroyOnHidden
            extra={
                <Button
                    type="primary"
                    icon={<IoSaveOutline size={19} />}
                    loading={store.submitting}
                    onClick={() => store.submit(notify, onCreated)}
                >
                    Зберегти
                </Button>
            }
        >
            {store.open && <DrawerDirtyGuard isDirty={() => store.isDirty} />}
            <div className={cx(styles, 'form')}>
                <FormSection
                    title="Географія"
                    hint={clusterCode ? undefined : 'Спершу оберіть кластер — регіони фільтруються за ним'}
                >
                    {shared('cluster')}
                    <MultiAssignmentFieldCell
                        fieldName="region"
                        label={fieldDef('region')['label']}
                        fieldDef={regionDef}
                        initialValue={store.shared['region'] ?? null}
                        onChange={onShared('region')}
                        disabled={!clusterCode}
                    />
                </FormSection>

                <FormSection title="Виконавець" hint="Усі поля, крім водіїв і ТЗ, спільні для всіх завдань">
                    {/* Обов'язковість перевіряє стор; тут — лише позначка в лейблі */}
                    {shared('dispatcher', { ...fieldDef('dispatcher'), isRequired: true })}
                    {shared('isHired')}
                    {store.isHired && shared('carrier', { ...fieldDef('carrier'), isRequired: true })}
                </FormSection>

                <FormSection
                    title="Водії й транспорт"
                    hint={
                        rowsLocked
                            ? 'Спершу оберіть перевізника — водії та ТЗ фільтруються за ним'
                            : 'Кожен рядок — окреме завдання; рядки без водія не створюються'
                    }
                >
                    <div className={cx(styles, 'rows')} role="list" aria-label="Водії й транспорт">
                        <div className={cx(styles, 'rowsHead')} aria-hidden="true">
                            <span>№</span>
                            <span>
                                Водій <span className={cx(styles, 'required')}>*</span>
                            </span>
                            <span>Транспортний засіб</span>
                            <span />
                        </div>
                        {store.rows.map((row, index) => (
                            <div key={row.id} className={cx(styles, 'row')} role="listitem">
                                <span className={cx(styles, 'rowNumber')}>{index + 1}</span>
                                <div className={cx(styles, 'rowCell')} data-label="Водій">
                                    <MultiAssignmentFieldCell
                                        key={`driver-${row.id}-${store.carrierIri ?? ''}-${store.isHired}`}
                                        fieldName="driver"
                                        fieldDef={{
                                            type: 'select',
                                            resource: 'Setting.User',
                                            inputProps: {
                                                filter: driverFilter,
                                                disabled: rowsLocked,
                                                'aria-label': `Водій, рядок ${index + 1}`,
                                                responseLabel: (item: any) =>
                                                    item?.fullName ||
                                                    `${item?.lastName || ''} ${item?.firstName || ''}`.trim() ||
                                                    '—',
                                            },
                                        }}
                                        initialValue={row.driver}
                                        onChange={(v) => store.setRowField(row.id, 'driver', v)}
                                        disabled={rowsLocked}
                                    />
                                </div>
                                <div className={cx(styles, 'rowCell')} data-label="Транспортний засіб">
                                    <MultiAssignmentFieldCell
                                        key={`vehicle-${row.id}-${store.carrierIri ?? ''}-${store.isHired}`}
                                        fieldName="vehicle"
                                        fieldDef={{
                                            type: 'select',
                                            resource: 'Transport.Vehicle',
                                            inputProps: {
                                                filter: vehicleFilter,
                                                disabled: rowsLocked,
                                                'aria-label': `Транспортний засіб, рядок ${index + 1}`,
                                                responseLabel: (item: any) =>
                                                    item?.licensePlate ||
                                                    item?.registrationNumber ||
                                                    item?.title ||
                                                    '—',
                                            },
                                        }}
                                        initialValue={row.vehicle}
                                        onChange={(v) => store.setRowField(row.id, 'vehicle', v)}
                                        disabled={rowsLocked}
                                    />
                                </div>
                                <Tooltip title={store.rows.length > 1 ? 'Видалити рядок' : undefined}>
                                    <Button
                                        type="text"
                                        className={cx(styles, 'rowRemove')}
                                        icon={<DeleteOutlined />}
                                        aria-label={`Видалити рядок ${index + 1}`}
                                        disabled={store.rows.length <= 1}
                                        onClick={() => store.removeRow(row.id)}
                                    />
                                </Tooltip>
                            </div>
                        ))}
                    </div>
                    <Button
                        type="dashed"
                        block
                        icon={<PlusOutlined />}
                        disabled={rowsLocked}
                        onClick={() => store.addRow()}
                    >
                        Додати водія
                    </Button>
                </FormSection>

                <FormSection title="Маршрут">
                    <MultiAssignmentFieldCell
                        fieldName="loadingPoint"
                        label={fieldDef('loadingPoint')['label']}
                        fieldDef={fieldDef('loadingPoint')}
                        onChange={onShared('loadingPoint')}
                        onRawChange={setLoadingPoint}
                    />
                    <MultiAssignmentFieldCell
                        fieldName="unloadingPoint"
                        label={fieldDef('unloadingPoint')['label']}
                        fieldDef={fieldDef('unloadingPoint')}
                        onChange={onShared('unloadingPoint')}
                        onRawChange={setUnloadingPoint}
                    />
                    <MultiAssignmentFieldCell
                        fieldName="typicalRoute"
                        label={fieldDef('typicalRoute')['label']}
                        fieldDef={typicalRouteDef}
                        initialValue={store.shared['typicalRoute'] ?? null}
                        onChange={onShared('typicalRoute')}
                    />
                    {shared('plannedLoadingDate')}
                </FormSection>

                <FormSection title="Вантаж">
                    {shared('cargo')}
                    {shared('totalWeightTonnes')}
                    {shared('tripsCount')}
                </FormSection>

                <FormSection title="Замовлення">
                    {shared('constructionObject')}
                    {shared('productionObject')}
                    {shared('responsibleCustomer')}
                </FormSection>
            </div>
        </AppDrawer>
    )
})
